import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, take } from 'rxjs';

import { RouterService } from './router.service';

@Injectable({
    providedIn: 'root',
})
export class TitleService {
    /**
     * Title
     * @type {BehaviorSubject<string>}
     */
    private title: BehaviorSubject<string>;

    /**
     * Renderer2 Instance (Can't be injected into service directly)
     * @type {Renderer2}
     */
    private renderer: Renderer2;

    public constructor(
        private titleService: Title,
        private routerService: RouterService,
        private translateService: TranslateService,
        rendererFactory: RendererFactory2,
        @Inject(PLATFORM_ID) protected platformId: any,
    ) {
        this.title = new BehaviorSubject<string>('');
        translateService.use('i18n');
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /**
     * Set page title
     * @param {string} title
     * @param {boolean} [noSuffix] Add or not common suffix (optional)
     * @param {string} pageTitle set a different title in service-header
     */
    public setTitle(title: string, noSuffix = false, pageTitle?: string): void {
        pageTitle ? this.title.next(pageTitle) : this.title.next(title);
        if (noSuffix === true) {
            this.setBrowserAndAccessibleTitle(title);
        } else {
            this.routerService
                .isCurrentRouteSosh()
                .pipe(take(1))
                .subscribe((isSosh: boolean) => {
                    const prefixKey: string = isSosh ? 'ecm.head.title.sosh' : 'ecm.head.title.orange';
                    this.translateService.get(prefixKey, { value: title }).subscribe((titleFull: string) => {
                        this.setBrowserAndAccessibleTitle(titleFull);
                    });
                });
        }
    }

    /**
     * Get current title without suffix
     * @returns {Observable<string>}
     */
    public getTitle(): Observable<string> {
        return this.title;
    }

    /**
     * Set the Browser Title (title tag) and the a11y container for SPA accessibility improvement
     * @param {string} title
     */
    private setBrowserAndAccessibleTitle(title: string): void {
        if (isPlatformBrowser(this.platformId)) {
            this.titleService.setTitle(title);
            const srPageTitleNode: HTMLElement = document.querySelector<HTMLElement>('#sr-page-title');
            if (srPageTitleNode !== null) {
                // remove existing children, can't be a while loop because renderer2 is asynchronous
                Array.from(srPageTitleNode.childNodes).forEach((child: HTMLElement) => {
                    this.renderer.removeChild(srPageTitleNode, child);
                });

                this.renderer.appendChild(srPageTitleNode, this.renderer.createText(title));
            }
        }
    }
}
